import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { selectCurrentUser } from 'features/auth/authSelectors';
import { getDefaultRoute } from 'utils/permissions';
import MainLayout from 'layouts/MainLayout';
import AuthLayout from 'layouts/AuthLayout';
import ProtectedRoute from './guards/ProtectedRoute';
import PublicRoute from './guards/PublicRoute';
import RoleRoute from './guards/RoleRoute';
import Loader from 'components/common/Loader';

import { ROUTES } from 'constants/routes';
import { MODULES, ACTIONS } from 'constants/permissions';

/* Lazy-loaded pages => route-based code splitting. */
const LoginPage = lazy(() => import('features/auth/pages/LoginPage'));
const DashboardPage = lazy(() => import('features/dashboard/pages/DashboardPage'));

const Stage1Page = lazy(() => import('features/stage1/pages/Stage1Page'));
const Stage2Page = lazy(() => import('features/stage2/pages/Stage2Page'));
const BriefingPage = lazy(() => import('features/briefing/pages/BriefingPage'));
const Storage1Page = lazy(() => import('features/storage1/pages/Storage1Page'));
const Storage2Page = lazy(() => import('features/storage2/pages/Storage2Page'));
const OcrStoragePage = lazy(() => import('features/ocrStorage/pages/OcrStoragePage'));
const NoStoragePage = lazy(() => import('features/noStorage/pages/NoStoragePage'));
const SearchPage = lazy(() => import('features/search/pages/SearchPage'));

const NewspaperMasterPage = lazy(() => import('features/settings/newspaperMaster/pages/NewspaperMasterPage'));
const RolesPage = lazy(() => import('features/settings/roles/pages/RolesPage'));
const RoleDetailPage = lazy(() => import('features/settings/roles/pages/RoleDetailPage'));
const UsersPage = lazy(() => import('features/settings/users/pages/UsersPage'));
const UserDetailPage = lazy(() => import('features/settings/users/pages/UserDetailPage'));
const ActivityLogsPage = lazy(() => import('features/settings/activityLogs/pages/ActivityLogsPage'));
const SystemConfigPage = lazy(() => import('features/settings/systemConfig/pages/SystemConfigPage'));

const NotFoundPage = lazy(() => import('features/misc/pages/NotFoundPage'));
const ForbiddenPage = lazy(() => import('features/misc/pages/ForbiddenPage'));

const IndexRedirect = () => {
  const user = useSelector(selectCurrentUser);
  return <Navigate to={getDefaultRoute(user)} replace />;
};

const AppRoutes = () => (
  <Suspense fallback={<Loader fullscreen label="Loading…" />}>
    <Routes>
      {/* Public */}
      <Route element={<PublicRoute />}>
        <Route element={<AuthLayout />}>
          <Route path={ROUTES.LOGIN} element={<LoginPage />} />
        </Route>
      </Route>

      {/* Protected app shell */}
      <Route element={<ProtectedRoute />}>
        <Route element={<MainLayout />}>
          <Route index element={<IndexRedirect />} />
          <Route path={ROUTES.SETTINGS} element={<Navigate to={ROUTES.SETTINGS_NEWSPAPERS} replace />} />

          <Route element={<RoleRoute module={MODULES.DASHBOARD} action={ACTIONS.VIEW} />}>
            <Route path={ROUTES.DASHBOARD} element={<DashboardPage />} />
          </Route>

          {/* Pipeline */}
          <Route element={<RoleRoute module={MODULES.STAGE1} action={ACTIONS.VIEW} />}>
            <Route path={ROUTES.STAGE1} element={<Stage1Page />} />
          </Route>
          <Route element={<RoleRoute module={MODULES.STAGE2} action={ACTIONS.VIEW} />}>
            <Route path={ROUTES.STAGE2} element={<Stage2Page />} />
          </Route>
          <Route element={<RoleRoute module={MODULES.BRIEFING} action={ACTIONS.VIEW} />}>
            <Route path={ROUTES.BRIEFING} element={<BriefingPage />} />
          </Route>
          <Route element={<RoleRoute module={MODULES.STORAGE1} action={ACTIONS.VIEW} />}>
            <Route path={ROUTES.STORAGE1} element={<Storage1Page />} />
          </Route>
          <Route element={<RoleRoute module={MODULES.STORAGE2} action={ACTIONS.VIEW} />}>
            <Route path={ROUTES.STORAGE2} element={<Storage2Page />} />
          </Route>
          <Route element={<RoleRoute module={MODULES.OCR_STORAGE} action={ACTIONS.VIEW} />}>
            <Route path={ROUTES.OCR_STORAGE} element={<OcrStoragePage />} />
          </Route>
          <Route element={<RoleRoute module={MODULES.NO_STORAGE} action={ACTIONS.VIEW} />}>
            <Route path={ROUTES.NO_STORAGE} element={<NoStoragePage />} />
          </Route>
          <Route element={<RoleRoute module={MODULES.SEARCH} action={ACTIONS.VIEW} />}>
            <Route path={ROUTES.SEARCH} element={<SearchPage />} />
          </Route>

          {/* Settings */}
          <Route element={<RoleRoute module={MODULES.NEWSPAPERS} action={ACTIONS.VIEW} />}>
            <Route path={ROUTES.SETTINGS_NEWSPAPERS} element={<NewspaperMasterPage />} />
          </Route>
          <Route element={<RoleRoute module={MODULES.ROLES} action={ACTIONS.VIEW} />}>
            <Route path={ROUTES.SETTINGS_ROLES} element={<RolesPage />} />
            <Route path={ROUTES.SETTINGS_ROLE_DETAIL} element={<RoleDetailPage />} />
          </Route>
          <Route element={<RoleRoute module={MODULES.USERS} action={ACTIONS.VIEW} />}>
            <Route path={ROUTES.SETTINGS_USERS} element={<UsersPage />} />
            <Route path={ROUTES.SETTINGS_USER_DETAIL} element={<UserDetailPage />} />
          </Route>
          <Route element={<RoleRoute module={MODULES.ACTIVITY_LOGS} action={ACTIONS.VIEW} />}>
            <Route path={ROUTES.SETTINGS_ACTIVITY_LOGS} element={<ActivityLogsPage />} />
          </Route>
          <Route element={<RoleRoute module={MODULES.SYSTEM_CONFIG} action={ACTIONS.VIEW} />}>
            <Route path={ROUTES.SETTINGS_SYSTEM_CONFIG} element={<SystemConfigPage />} />
          </Route>

          <Route path={ROUTES.FORBIDDEN} element={<ForbiddenPage />} />
        </Route>
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  </Suspense>
);

export default AppRoutes;
